import { Component, OnInit, inject, signal } from '@angular/core';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ThemeService } from '@shared/services/theme.service';
import { BRAND_ASSETS } from '@shared/constants/brand-assets.constant';
import { LoadingPageComponent } from '@shared/components/loading-page/loading-page.component';
import { ModalStackService } from '@shared/services/modal/modal-stack.service';
import { AppBootService } from '@shared/services/app-boot.service';
import { StorefrontApiService } from '@coreshop/feature-storefront/services/storefront-api.service';
import { StorefrontContextService } from '@coreshop/feature-storefront/services/storefront-context.service';

const ADMIN_LOGO = BRAND_ASSETS.icon;
const ADMIN_TITLE = 'CoreShop';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterModule,
    LoadingPageComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit {
  title = 'CoreShop';
  isLoading = signal(true);
  showLoadingComponent = signal(true);

  readonly splashVariant = signal<'admin' | 'store'>('admin');
  readonly splashTitle = signal(ADMIN_TITLE);
  readonly splashLogo = signal<string | null>(ADMIN_LOGO);

  private router = inject(Router);
  private modalStack = inject(ModalStackService);
  private readonly appBoot = inject(AppBootService);
  private storefrontApi = inject(StorefrontApiService);
  private storefrontCtx = inject(StorefrontContextService);
  /** ThemeService must stay injected so theme init runs on boot. */
  private readonly _theme = inject(ThemeService);

  private pageLoaded = false;
  private splashReady = false;
  private hideScheduled = false;

  ngOnInit() {
    this.configureBootSplash(this.currentPath());

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      const modalsCount = this.modalStack.getOpenModalsCount();
      if (modalsCount === 0 && document.body.style.overflow === 'hidden') {
        console.warn('[AppComponent] Limpiando overflow residual en cambio de ruta');
        document.body.style.overflow = '';
      }
    });

    if (typeof window !== 'undefined') {
      (window as any)['modalStackDebug'] = () => this.modalStack.getDebugInfo();
      (window as any)['modalStackReset'] = () => this.modalStack.reset();
    }

    if (document.readyState === 'complete') {
      this.markPageLoaded();
    } else {
      window.addEventListener('load', () => this.markPageLoaded());
    }

    // Fallback: no bloquear splash si el tenant tarda demasiado
    setTimeout(() => this.markSplashReady(), 2500);
  }

  private currentPath(): string {
    if (typeof window !== 'undefined' && window.location?.pathname) {
      return window.location.pathname;
    }
    return this.router.url?.split('?')[0] ?? '/';
  }

  private configureBootSplash(path: string): void {
    const storeMatch = path.match(/\/store\/([^/]+)/);
    if (!storeMatch) {
      this.splashVariant.set('admin');
      this.splashTitle.set(ADMIN_TITLE);
      this.splashLogo.set(ADMIN_LOGO);
      this.markSplashReady();
      return;
    }

    const slug = decodeURIComponent(storeMatch[1]);
    this.splashVariant.set('store');
    this.splashTitle.set(this.storefrontCtx.storeName() || slug.replace(/-/g, ' '));
    this.splashLogo.set(this.storefrontCtx.logoUrl() || null);

    if (/^\d+$/.test(slug)) {
      this.markSplashReady();
      return;
    }

    if (this.storefrontCtx.storeSlug() === slug && this.storefrontCtx.companyId() > 0) {
      this.applyStoreSplash(
        this.storefrontCtx.storeName() || slug,
        this.storefrontCtx.logoUrl(),
      );
      this.markSplashReady();
      return;
    }

    this.storefrontApi.resolveTenant(slug).subscribe({
      next: tenant => {
        this.storefrontCtx.setTenant(tenant);
        this.applyStoreSplash(tenant.storeName, tenant.logoUrl);
        this.markSplashReady();
      },
      error: () => {
        this.splashTitle.set(slug.replace(/-/g, ' '));
        this.splashLogo.set(null);
        this.markSplashReady();
      },
    });
  }

  private applyStoreSplash(name: string, logoUrl: string | null | undefined): void {
    this.splashVariant.set('store');
    this.splashTitle.set((name || '').trim() || 'Tienda');
    this.splashLogo.set(logoUrl?.trim() || null);
  }

  private markPageLoaded(): void {
    this.pageLoaded = true;
    this.tryHideLoading();
  }

  private markSplashReady(): void {
    this.splashReady = true;
    this.tryHideLoading();
  }

  private tryHideLoading(): void {
    if (!this.pageLoaded || !this.splashReady || this.hideScheduled) return;
    this.hideScheduled = true;
    setTimeout(() => {
      this.isLoading.set(false);
      this.appBoot.markReady();
      setTimeout(() => this.showLoadingComponent.set(false), 800);
    }, 400);
  }
}
